import { createApp } from "vue";
import App from "./App.vue";
import router from "./index.ts";
import { ConfigService } from "@/services/ConfigService";
import { GlobalConstantService } from "./services/GlobalConstantService";
import { bootstrapReady } from "./bootstrapState.ts";
import { EcxToastPlugin, EcxConfirmPlugin } from "@edgeconnex/design-system";

// App CSS
import "./styles.css";

// CASL
import { ability } from "./ability.ts";
import { AbilityService } from "./services/AbilityService";
import { abilitiesPlugin } from "@casl/vue";

import { msalObj } from './entraConfig';
import { handleEntra } from './handleEntra';
import { LoggedUserService } from "@/services/LoggedUserService";
import { refreshAuthenticatedSessionState } from "@/services/sessionBootstrap";
import { UserPreferencesService } from "@/services/UserPreferencesService";
import { resolveColorScheme } from "@/composables/useTheme";
import { TelemetryService } from "@/services/TelemetryService";
import { SessionReplayService } from "@/services/SessionReplayService";

const BOOTSTRAP_AUTH_ERROR =
  "Could not connect to CMMS. Please check your network connection and sign in again.";

function clearSessionForBootstrapFailure() {
  sessionStorage.setItem("authError", BOOTSTRAP_AUTH_ERROR);
  sessionStorage.removeItem("sessionToken");
  sessionStorage.removeItem("sessionExpire");
}

async function loadAuthenticatedState() {
  if (!sessionStorage.getItem("sessionToken")) {
    return;
  }

  await refreshAuthenticatedSessionState();

  // A role-less / non-ECX impersonation target can't load a user (GetBySessionToken is login-gated) but the session is valid: keep it so the app lands on /forbidden with a working banner + Stop instead of bouncing to login (#5076).
  if (!LoggedUserService.user.value && !LoggedUserService.impersonation.value) {
    throw new Error("Unable to load logged-in user during bootstrap.");
  }

  // Apply the user's saved theme preference (cross-device). App.vue already painted from the
  // per-device localStorage value for the first frame; this reconciles it to the server value
  // ('system' resolves against the OS scheme). The cache stores the raw preference.
  const colorScheme = UserPreferencesService.getColorScheme();
  if (colorScheme) {
    document.documentElement.classList.toggle("my-app-dark", resolveColorScheme(colorScheme) === "dark");
    window.localStorage.setItem("theme", colorScheme);
  }
}

//---------------------------------------------------------------------------------------------------
async function bootstrap() {
  const app = createApp(App);

  // Load config before mounting (needed for API base URL)
  await ConfigService.loadConfig();

  TelemetryService.init();
  TelemetryService.trackRouter(router);
  SessionReplayService.init();

  app.config.errorHandler = (err, instance, info) => {
    TelemetryService.trackException(err, {
      component: instance?.$options?.name ?? 'unknown',
      lifecycleHook: info ?? 'unknown',
    });
  };

  app.use(abilitiesPlugin, ability, {
    useGlobalProperties: true,
  });

  app.config.globalProperties.$ability = ability;
  app.config.globalProperties.$abilityService = AbilityService;
  app.config.globalProperties.$loggedUser = LoggedUserService.user;
  app.config.globalProperties.$loggedUserService = LoggedUserService;
  app.config.globalProperties.$userList = GlobalConstantService.userInfoList;
  app.config.globalProperties.$orgList = GlobalConstantService.orgInfoList;
  app.config.globalProperties.$siteInfoList = GlobalConstantService.siteInfoList;
  app.config.globalProperties.$globalConstantService = GlobalConstantService;

  app.provide("ability", ability);
  app.provide("abilityService", AbilityService);
  app.provide("loggedUserService", LoggedUserService);
  app.provide("globalConstantService", GlobalConstantService);

  app.use(router);
  app.use(EcxToastPlugin);
  app.use(EcxConfirmPlugin);

  // Mount immediately — App.vue shows AppLoading while bootstrapReady is false
  app.mount("#app");

  // Async bootstrap: MSAL + service loading runs while AppLoading is visible
  try {
    await msalObj.initialize();
    const { handleRedirect } = handleEntra();
    await handleRedirect();
  } catch {
    sessionStorage.setItem("authError", "Could not start sign-in. Please try again.");
  }

  try {
    await loadAuthenticatedState();
  } catch {
    clearSessionForBootstrapFailure();
  } finally {
    bootstrapReady.value = true;
  }
}
// Call bootstrap
await bootstrap().catch(() => undefined);
